import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, NgxsSimpleChange, Store } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { ExpenseEntryActions } from '../actions/expense-entry.actions';
import { ExpenseEntry } from '../models/expense-entry.model';
import { ExpenseEntryService } from 'src/app/services/expense-entry.service';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { ConnectionStatusEnum, NetworkService } from 'src/app/services/network.service';
import { OfflineService } from 'src/app/services/offline.service';
import { DateTime } from 'luxon';
import { StorageService } from 'src/app/services/storage.service';
import { app_date_time_format } from 'src/app/globals';

export class ExpenseEntryStateModel {
    is_refreshing: boolean
    pending_count: number
    last_update: string
    expense_entries: ExpenseEntry[]
}

@State<ExpenseEntryStateModel>({
    name: 'expense_entry',
    defaults: {
        is_refreshing: true,
        pending_count: 0,
        last_update: '',
        expense_entries: []
    }
})
@Injectable()
export class ExpenseEntryState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private expenseEntryService: ExpenseEntryService,        
        private networkService: NetworkService,
        private offline: OfflineService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<ExpenseEntryStateModel>) {   
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {                 
                        await this.reloadStateFromStorage()   
                        this.store.dispatch(new ExpenseEntryActions.IncrementPendingCount())
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {            
        if (this.storageService.is_initialized) {      
            const state_model: ExpenseEntryStateModel = change.currentValue      
            this.storageService.set('expense_entries', JSON.stringify(state_model))    
        }      
        if (this.networkService.currentStatus === ConnectionStatusEnum.Online) {            
            if (change.previousValue && change.previousValue.pending_count < change.currentValue.pending_count) {                
                const state_model: ExpenseEntryStateModel = change.currentValue
                await this.offline.processPendingExpenseEntries(state_model.expense_entries)                    
            } 
        }   
    }
    
    async reloadStateFromStorage() {        
        const json: string = await this.storageService.get('expense_entries');        
        if (json) {
            const state_model: ExpenseEntryStateModel = JSON.parse(json)            
            if (state_model) {                
                this.store.dispatch(new ExpenseEntryActions.Init(state_model));
                return
            } 
        }
        this.store.dispatch(new ExpenseEntryActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        const objs = await this.expenseEntryService.getMine()    
        if (objs != null) {
          const many = ExpenseEntry.buildMany(objs)                     
          this.store.dispatch(new ExpenseEntryActions.Merge(many))
          return true
        }
        return false
    }
  
    @Selector()
    static getLastUpdate(state: ExpenseEntryStateModel) {
        return state.last_update
    }

    @Selector()
    static getIsRefreshing(state: ExpenseEntryStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getState(state: ExpenseEntryStateModel) {
        return state
    }

    @Selector()
    static getAll(state: ExpenseEntryStateModel) {
        return state.expense_entries
    }

    @Selector()
    static getOne(state: ExpenseEntryStateModel) {
        return (id: number) => {
            return state.expense_entries.filter(s => s.id === id)
        }
    }

    @Action(ExpenseEntryActions.Refresh)
    async refresh({patchState}: StateContext<ExpenseEntryStateModel>, {  }:ExpenseEntryActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false})
        }
    }

    @Action(ExpenseEntryActions.IncrementPendingCount)
    incrementPendingCount({patchState, getState}: StateContext<ExpenseEntryStateModel>, {  }:ExpenseEntryActions.IncrementPendingCount) {     
        const state = getState()
        patchState({
            pending_count: state.pending_count + 1
        })
    }
    
    @Action(ExpenseEntryActions.Init)
    init({setState, getState}: StateContext<ExpenseEntryStateModel>, { payload }:ExpenseEntryActions.Init) {                  
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new ExpenseEntryActions.Refresh())
    }
    
    @Action(ExpenseEntryActions.Set)
    set({setState, getState}: StateContext<ExpenseEntryStateModel>, { payload }:ExpenseEntryActions.Set) {      
        const state = getState()        
        setState({   
            is_refreshing: state.is_refreshing, 
            pending_count: 0,
            last_update: DateTime.now().toFormat(app_date_time_format),         
            expense_entries: payload
        })
    }

    @Action(ExpenseEntryActions.Merge)
    merge({getState, setState}: StateContext<ExpenseEntryStateModel>, { payload }:ExpenseEntryActions.Merge) {   
        // This is to merge data from the API provided in payload, into the app state.
        // Basically, if we have server data, we trust that, so we want to replace the 
        // current app state with the payload, except:
        // 1. Any objects pending upload or delete need to be merged in, even if it no longer exists.
        // 2. Same goes for any with a service_entry that is pending approval upload.
        const state = getState()
        const oldExpenseEntries = [...state.expense_entries]
        let newExpenseEntries = [...payload]      

        // For each wor in state now...
        for (const expense_entry of oldExpenseEntries) {     
            // If wor id is not in the list from the server       
            let newExpenseEntry = payload.find(f => f.id === expense_entry.id)   
            if (!newExpenseEntry) {                
                // If there are any pending save/delete, keep the entire object in state, else do not since no longer on server                
                if (expense_entry.is_pending_save || expense_entry.is_pending_delete) {                 
                    newExpenseEntries.push(expense_entry)
                }
            } else {                
                // Object in state is in the payload from the server. 
                // 1. If anything is pending, we need to preserve the one in state, first removing the one that is from the server
                // 1. Else we leave the one from the server to set in the new state
                if (expense_entry.is_pending_save || expense_entry.is_pending_delete) {
                    // Remove one that is from the server
                    newExpenseEntries = newExpenseEntries.filter((obj) => {return obj !== newExpenseEntry});                        
                    // Add the one from state back in
                    newExpenseEntries.push(expense_entry)
                }
            }
        }

        setState({
            is_refreshing: false,
            pending_count: 0,
            last_update: DateTime.now().toFormat(app_date_time_format),
            expense_entries: newExpenseEntries
        })
    }

    @Action(ExpenseEntryActions.AddOne)
    addOne({getState, patchState}: StateContext<ExpenseEntryStateModel>, { payload }:ExpenseEntryActions.AddOne) {
        const state = getState()              
        patchState({
            expense_entries: [...state.expense_entries, payload]
        })
    }

    @Action(ExpenseEntryActions.UpdateOne)
    updateOne({setState}: StateContext<ExpenseEntryStateModel>, { payload }:ExpenseEntryActions.UpdateOne) {
        setState(
            patch<ExpenseEntryStateModel>
                ({
                    expense_entries: updateItem((item: ExpenseEntry) => item.id === payload.id, 
                        patch<ExpenseEntry>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(ExpenseEntryActions.DeleteOne)
    deleteOne({setState}: StateContext<ExpenseEntryStateModel>, { payload }:ExpenseEntryActions.DeleteOne) {
        setState(
            patch<ExpenseEntryStateModel>
                ({
                    expense_entries: updateItem((item: ExpenseEntry) => item.id === payload.id, 
                        patch<ExpenseEntry>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(ExpenseEntryActions.ApiAddedOne)
    apiAddedOne({ setState }: StateContext<ExpenseEntryStateModel>, { payload }:ExpenseEntryActions.ApiAddedOne) {         
        setState(
            patch<ExpenseEntryStateModel>
                ({
                    expense_entries: updateItem((item: ExpenseEntry) => item.uuid === payload.uuid, 
                        patch<ExpenseEntry>
                            ({
                                id: payload.id,
                                is_current_pay_period: payload.is_current_pay_period,
                                is_pending_save: false                           
                            })
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(ExpenseEntryActions.ApiUpdatedOne)
    apiUpdatedOne({ setState }: StateContext<ExpenseEntryStateModel>, { payload }:ExpenseEntryActions.ApiUpdatedOne) {         
        setState(
            patch<ExpenseEntryStateModel>
                ({
                    expense_entries: updateItem((item: ExpenseEntry) => item.id === payload.id, 
                        patch<ExpenseEntry>
                            ({
                                is_pending_save: false                           
                            })
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(ExpenseEntryActions.ApiDeletedOne)
    apiDeletedOne({ setState }: StateContext<ExpenseEntryStateModel>, { payload }:ExpenseEntryActions.ApiDeletedOne) {         
        setState(
            patch<ExpenseEntryStateModel>
                ({
                    expense_entries: removeItem((item: ExpenseEntry) => item.id === payload.id),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }
}
